/**
 * M6 — Gráfico de série temporal das leituras dos instrumentos.
 * Fiel ao protótipo V8 + slide do dono (GUPM-211): INA/PZ/MS/MSE e, desde o GUPM-270, TILT; filtros
 * Estrutura/Tipo/Seção, chip com faixa da barragem, gráfico com cotas + níveis de
 * controle + régua + pluviometria + zoom X/Y.
 */
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, damColor } from '@/ui';
import { useDams } from '@/hooks/useDams';
import { useAuth } from '@/hooks/useAuth';
import { filterInstrumentsByDam } from '@/domain/instrumentos/dam-filter';
import { SEM_LEITURA } from '@/domain/instrumentos/nivel-exibido';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';
import { useInstrumentSeries } from './hooks/useInstrumentSeries';
import { useSelecaoDoM6 } from './hooks/useSelecaoDoM6';
import {
  applyInstrumentFilters, computeTrend, filterMonitoredInstruments, temGraficoDeSerie, toChartSeries,
} from './domain/chart-data';
import { buildInstrumentChartModel } from './domain/chart-model';
import FilterBar from './components/FilterBar';
import InstrumentChipBar from './components/InstrumentChipBar';
import InstrumentChart from './components/InstrumentChart';
import InstrumentDetail from './components/InstrumentDetail';
import HistoricoLeituras from './components/HistoricoLeituras';
import { useHistoricoInstrumento } from './hooks/useHistoricoInstrumento';

/** Cor da linha de leitura (amarelo JF, como no modo single do V8). */
const READING_COLOR = '#FECB00';

/**
 * GUPM-267 — entrada manual de leitura suspensa.
 *
 * `submitReading` não chama `classify_reading_level`: o `level` fica com o default
 * da coluna (`NORMAL`) qualquer que seja o valor, e a tela de exibição repete o
 * gravado. Uma leitura em faixa de emergência apareceria como Normal no mapa, no
 * dashboard e no semáforo — pior que não ter a entrada manual. A rota
 * `/instrumentacao/leitura` continua existindo (e ainda gated por RoleGuard);
 * suspenso aqui é o atalho que a expõe.
 *
 * Remover esta constante ao fechar o GUPM-267.
 */
const LEITURA_MANUAL_SUSPENSA = true;

export default function M6Grafico() {
  // C-24 — o sino (leitura atrasada) pode pedir um instrumento.
  const { selectedId, setSelectedId, filters, setFilters } = useSelecaoDoM6();
  const {
    instruments, levelById, currentById, series, seriesEixoB, faixaCanal,
    reguaSeries, pluviSeries, isLoading, isLoadingSeries, error,
  } = useInstrumentSeries(selectedId);
  const { data: dams = [] } = useDams();
  const { profile } = useAuth();
  // ADR-0009: operador é read-only — só RT/admin veem o atalho de inserção manual.
  const canWrite = profile === 'tecnico_responsavel' || profile === 'admin';
  // Dois gates independentes: permissão (ADR-0009) e disponibilidade do recurso
  // (GUPM-267). Separados de propósito — mexer num não deve mascarar o outro.
  const podeInserirLeitura = canWrite && !LEITURA_MANUAL_SUSPENSA;

  // Só tipos monitorados (`MONITORED_TYPES`: INA/PZ/MS/MSE/TILT); demais seguem como overlay.
  const monitored = useMemo(() => filterMonitoredInstruments(instruments), [instruments]);

  // Seções disponíveis na estrutura filtrada (alimenta o select de Seção).
  const sections = useMemo(() => {
    const scoped = filterInstrumentsByDam(monitored, filters.dam);
    return [...new Set(scoped.map((i) => i.section || '—'))].filter((s) => s && s !== '—').sort();
  }, [monitored, filters.dam]);

  const visible = useMemo(() => applyInstrumentFilters(monitored, filters), [monitored, filters]);

  // Cor de identidade por barragem (faixa esquerda dos chips).
  const damColorById = useMemo(
    () => Object.fromEntries(dams.map((d) => [d.id, damColor(d.code)])),
    [dams],
  );

  // Seleciona o primeiro visível quando a lista carrega, ou quando o filtro tira o
  // selecionado do recorte.
  useEffect(() => {
    if (visible.length === 0) return;
    if (!selectedId || !visible.some((i) => i.id === selectedId)) {
      setSelectedId(visible[0]!.id);
    }
  }, [selectedId, setSelectedId, visible]);

  const selected = useMemo(
    () => visible.find((i) => i.id === selectedId) ?? null,
    [visible, selectedId],
  );
  const {
    historico, isLoading: isLoadingHistorico, error: historicoErro,
  } = useHistoricoInstrumento(selected);
  const damLabel = useMemo(() => {
    const code = selected ? dams.find((d) => d.id === selected.damId)?.code : undefined;
    // GUPM-329 — a Mina tem marcos superficiais; "Barragem MINA" não existe.
    return rotuloDaEstrutura(code) ?? undefined;
  }, [selected, dams]);
  const trend = useMemo(() => computeTrend(toChartSeries(series)), [series]);
  const model = useMemo(
    () => (selected
      ? buildInstrumentChartModel({
        instrument: selected, series, reguaSeries, pluviSeries, seriesEixoB, faixaCanal,
      })
      : null),
    [selected, series, reguaSeries, pluviSeries, seriesEixoB, faixaCanal],
  );

  return (
    <div>
      <PageHeader
        title="📈 Instrumentação"
        subtitle="Série temporal das leituras × limites de controle"
        actions={podeInserirLeitura ? (
          <Link
            to="/instrumentacao/leitura"
            className="shrink-0 px-3 py-2 rounded-lg text-sm font-bold border border-jf-border bg-jf-surface text-white hover:text-jf-yellow"
          >
            ✏️ Inserir leitura manual
          </Link>
        ) : null}
      />

      {isLoading && <p className="text-jf-muted text-sm">Carregando instrumentos…</p>}

      {error && (
        <div className="text-level-emergencia rounded-xl border border-level-emergencia/40 bg-level-emergencia/10 p-4">
          <p className="font-bold">Erro ao carregar leituras</p>
          <p className="text-sm mt-1 text-jf-muted">{error.message}</p>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <FilterBar
            dams={dams}
            sections={sections}
            filters={filters}
            onChange={setFilters}
            className="mb-3"
          />

          {visible.length === 0 && (
            <p className="text-jf-muted text-sm rounded-xl border border-jf-border bg-jf-surface/60 px-3 py-4">
              Sem instrumentos para o recorte selecionado.
            </p>
          )}

          <InstrumentChipBar
            instruments={visible}
            levelById={levelById}
            damColorById={damColorById}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />

          {selected && model && (
            <div className="rounded-xl border border-jf-border bg-jf-surface p-4 mt-3">
              <InstrumentDetail
                instrument={selected}
                damLabel={damLabel}
                level={levelById[selected.id] ?? SEM_LEITURA}
                current={currentById[selected.id]}
                trend={trend}
                faixaCanal={faixaCanal}
              />
              {temGraficoDeSerie(selected.type) && (
                <InstrumentChart
                  key={selected.id}
                  model={model}
                  color={READING_COLOR}
                  loading={isLoadingSeries}
                />
              )}
              {historicoErro && (
                <p className="text-level-emergencia text-sm mt-3">
                  Erro ao carregar o histórico: {historicoErro.message}
                </p>
              )}
              {historico && !historicoErro && (
                <HistoricoLeituras historico={historico} loading={isLoadingHistorico} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
